import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Fab,
  Tooltip,
} from "@material-ui/core";
import { Feedback, Send } from "@material-ui/icons";
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import { useTranslation } from "react-i18next";
import { client } from "services/axiosClient";

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "fixed",
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 1200,
    backgroundColor: "#790000",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#b50000",
    },
  },
  dialogPaper: {
    minWidth: "400px",
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
}));

export default function FeedbackButton({ pageOrigin }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState("feedback");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setSubmitted(false);
  };

  const handleClose = () => {
    setOpen(false);
    setFeedbackType("feedback");
    setContent("");
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;

    setLoading(true);
    try {
      await client.post("/api/ctsguide/feedback/", {
        feedback_type: feedbackType,
        page_origin: pageOrigin,
        content: content,
      });

      setSubmitted(true);
      setContent("");

      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title={t("ctsguide.feedback.button_tooltip")} placement="left">
        <Fab className={classes.fab} onClick={handleOpen}>
          <Feedback />
        </Fab>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.dialogPaper }}
      >
        <ModalHeader
          title={t("ctsguide.feedback.title")}
          onClose={handleClose}
          closeLabel={t("common.close")}
        />

        <DialogContent>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <p>{t("ctsguide.feedback.success")}</p>
            </div>
          ) : (
            <>
              <TextField
                select
                fullWidth
                label={t("ctsguide.feedback.type")}
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
                className={classes.formField}
                variant="outlined"
              >
                <MenuItem value="question">
                  {t("ctsguide.feedback.types.question")}
                </MenuItem>
                <MenuItem value="feedback">
                  {t("ctsguide.feedback.types.feedback")}
                </MenuItem>
                <MenuItem value="suggestion">
                  {t("ctsguide.feedback.types.suggestion")}
                </MenuItem>
              </TextField>

              <TextField
                fullWidth
                multiline
                rows={4}
                label={t("ctsguide.feedback.content")}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                variant="outlined"
                placeholder={t("ctsguide.feedback.placeholder")}
              />
            </>
          )}
        </DialogContent>

        {!submitted && (
          <DialogActions>
            <Button onClick={handleClose} simple>
              {t("common.cancel")}
            </Button>
            <Button
              color="rose"
              onClick={handleSubmit}
              disabled={loading || !content.trim()}
              startIcon={<Send />}
            >
              {loading ? t("common.sending") : t("common.send")}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}

FeedbackButton.propTypes = {
  pageOrigin: PropTypes.string,
};
